import DungeonConfig, { ROOM_TYPE, ENEMY_TYPES, ENEMY_GROUPS, THEME_ENEMY_POOLS } from './DungeonConfig.js';
import CombatStats from '../combat/CombatStats.js';
import { isLPCPreset, resolvePreset } from '../npc/LPCEnemyPresets.js';
import { generateRandomAppearance } from '../character/LPCRandomAppearance.js';
import SeededRandom from '../utils/SeededRandom.js';

/**
 * DungeonPopulator - Places enemies, loot spawn points, and objectives
 * based on room types and level difficulty scaling.
 */
export default class DungeonPopulator {
  /**
   * Populate a DungeonLevel with spawn points and objectives.
   * @param {DungeonLevel} level
   * @param {Object} levelConfig - from DungeonConfig.getLevelConfig()
   * @param {Function} rng - seeded random function
   */
  static populate(level, levelConfig, rng, themeName) {
    const spawnPoints = [];

    // Build set of blocked positions (pillars + buffer, chests, room centers)
    // Uses 3x3 buffer around pillars so characters don't visually overlap them
    const blocked = new Set();
    for (const room of level.rooms) {
      // Pillar positions with 1-tile buffer (same offsets as DungeonGeometry)
      if (room.width >= 8 && room.depth >= 8) {
        const offsets = [
          { dx: 2, dz: 2 }, { dx: room.width - 3, dz: 2 },
          { dx: 2, dz: room.depth - 3 }, { dx: room.width - 3, dz: room.depth - 3 }
        ];
        for (const off of offsets) {
          const px = room.x + off.dx;
          const pz = room.z + off.dz;
          // Block 3x3 area around each pillar
          for (let bx = -1; bx <= 1; bx++) {
            for (let bz = -1; bz <= 1; bz++) {
              blocked.add(`${px + bx},${pz + bz}`);
            }
          }
        }
      }
      // Center positions (portals, chests, items) — also 3x3 buffer
      const cx = room.x + Math.floor(room.width / 2);
      const cz = room.z + Math.floor(room.depth / 2);
      for (let bx = -1; bx <= 1; bx++) {
        for (let bz = -1; bz <= 1; bz++) {
          blocked.add(`${cx + bx},${cz + bz}`);
        }
      }
    }

    for (const room of level.rooms) {
      // Pick a themed enemy group for this room (consistent enemies per room)
      const themePool = themeName ? THEME_ENEMY_POOLS[themeName] : null;
      let group;
      if (themePool) {
        // Weighted random selection from theme's preferred groups
        const roll = rng();
        let cumulative = 0;
        let picked = themePool.groups[0];
        for (let gi = 0; gi < themePool.groups.length; gi++) {
          cumulative += themePool.weights[gi];
          if (roll < cumulative) { picked = themePool.groups[gi]; break; }
        }
        group = ENEMY_GROUPS[picked];
      } else {
        group = ENEMY_GROUPS[Math.floor(rng() * ENEMY_GROUPS.length)];
      }
      // Pick one skin/hair color for the whole room (consistency within group)
      // Theme overrides take priority over group defaults
      const skinPool = (themePool && themePool.skinOverride) || group.skinColors;
      const hairPool = (themePool && themePool.hairOverride) || group.hairColors;
      const roomSkin = skinPool
        ? skinPool[Math.floor(rng() * skinPool.length)]
        : null;
      const roomHair = hairPool
        ? hairPool[Math.floor(rng() * hairPool.length)]
        : null;

      switch (room.type) {
        case ROOM_TYPE.START:
          // Entry point — center of start room
          level.entryPoint = {
            x: room.x + Math.floor(room.width / 2),
            y: 0,
            z: room.z + Math.floor(room.depth / 2)
          };
          // Friendly guide NPC near entry
          DungeonPopulator._addFriendlyNPC(spawnPoints, room, rng, 'guide', blocked);
          break;

        case ROOM_TYPE.EXIT:
          // Exit point — center of exit room
          level.exitPoint = {
            x: room.x + Math.floor(room.width / 2),
            y: 0,
            z: room.z + Math.floor(room.depth / 2)
          };
          // Light guard near exit
          DungeonPopulator._addEnemies(spawnPoints, room, 1, 2, levelConfig, rng, 'common', blocked, group, roomSkin, roomHair);
          break;

        case ROOM_TYPE.NORMAL:
          DungeonPopulator._addEnemies(
            spawnPoints, room,
            levelConfig.minEnemies, levelConfig.maxEnemies,
            levelConfig, rng, 'common', blocked, group, roomSkin, roomHair
          );
          break;

        case ROOM_TYPE.TREASURE:
          // Fewer enemies guarding treasure
          DungeonPopulator._addEnemies(spawnPoints, room, 1, 2, levelConfig, rng, 'elite', blocked, group, roomSkin, roomHair);
          // Chest is handled by DungeonGeometry (cuboid), loot spawned on open
          break;

        case ROOM_TYPE.BOSS:
          // Boss + minions from same group
          DungeonPopulator._addBoss(spawnPoints, room, levelConfig, rng, blocked, group, roomSkin, roomHair);
          DungeonPopulator._addEnemies(spawnPoints, room, 1, 2, levelConfig, rng, 'common', blocked, group, roomSkin, roomHair);
          break;

        case ROOM_TYPE.SHRINE:
          // Shrine rooms have a healing potion spawn, no enemies
          spawnPoints.push({
            x: room.x + Math.floor(room.width / 2),
            y: 0,
            z: room.z + Math.floor(room.depth / 2),
            type: 'item',
            data: { itemId: 'health-potion', quantity: 2 }
          });
          // Friendly healer NPC in shrine
          DungeonPopulator._addFriendlyNPC(spawnPoints, room, rng, 'healer', blocked);
          break;
      }
    }

    level.spawnPoints = spawnPoints;

    // Generate objectives
    level.objectives = DungeonPopulator._generateObjectives(level);

    return level;
  }

  /**
   * Add enemy spawn points inside a room.
   * @private
   */
  static _addEnemies(spawnPoints, room, minCount, maxCount, levelConfig, rng, tier, blocked, group, roomSkin, roomHair) {
    const count = minCount + Math.floor(rng() * (maxCount - minCount + 1));
    // Use group's tier pool if available, fall back to global ENEMY_TYPES
    const types = (group && group[tier]) || ENEMY_TYPES[tier] || ENEMY_TYPES.common;

    for (let i = 0; i < count; i++) {
      // Random position inside room, 2-tile margin from walls to avoid spawning in walls
      let x, z;
      const margin = 2;
      const maxAttempts = 10;
      let attempts = 0;
      do {
        x = room.x + margin + Math.floor(rng() * Math.max(1, room.width - margin * 2));
        z = room.z + margin + Math.floor(rng() * Math.max(1, room.depth - margin * 2));
        attempts++;
      } while (blocked && blocked.has(`${x},${z}`) && attempts < maxAttempts);

      // Skip this enemy if we couldn't find a valid position
      if (blocked && blocked.has(`${x},${z}`)) continue;

      // Mark position as blocked for subsequent spawns
      if (blocked) blocked.add(`${x},${z}`);

      const characterName = types[Math.floor(rng() * types.length)];

      // LPC preset: resolve appearance + equipment + combat from preset
      if (isLPCPreset(characterName)) {
        const seed = SeededRandom.hashString(`${room.id}_enemy_${i}`);
        const enemyLevel = tier === 'elite'
          ? (levelConfig.targetLevel || 1) + 1
          : (levelConfig.targetLevel || 1);
        const resolved = resolvePreset(characterName, seed, roomSkin, roomHair);
        // Build attribute-based stats from archetype
        const npcStats = CombatStats.buildNPCStats(enemyLevel, resolved.archetype, false);

        const combatConfig = {
          ...resolved.combatConfig,
          ...npcStats,
          level: enemyLevel,
          isHostile: true
        };

        spawnPoints.push({
          x, y: 0, z,
          type: 'enemy',
          data: {
            characterName,
            lpcAppearance: resolved.lpcAppearance,
            lpcEquipment: resolved.lpcEquipment,
            displayName: resolved.displayName,
            combatConfig,
            roomId: room.id
          }
        });
        continue;
      }

      // Legacy path: fixed character models
      const enemyLevel = tier === 'elite'
        ? (levelConfig.targetLevel || 1) + 1
        : (levelConfig.targetLevel || 1);
      const scaled = CombatStats.scaleForLevel({ maxHealth: 30, attackPower: 5, defense: 1 }, enemyLevel);

      const combatConfig = {
        ...scaled,
        level: enemyLevel,
        aggroRange: 8,
        deaggroRange: 14,
        attackRange: 1.8,
        attackCooldown: 1.2,
        lootTable: tier === 'elite' ? 'elite' : 'default',
        isHostile: true
      };

      spawnPoints.push({
        x, y: 0, z,
        type: 'enemy',
        data: {
          characterName,
          combatConfig,
          roomId: room.id
        }
      });
    }
  }

  /**
   * Add a boss enemy at room center (or nearby unblocked tile).
   * @private
   */
  static _addBoss(spawnPoints, room, levelConfig, rng, blocked, group, roomSkin, roomHair) {
    // Use group's boss pool if available, fall back to global
    const types = (group && group.boss) || ENEMY_TYPES.boss;
    const characterName = types[Math.floor(rng() * types.length)];

    const bossLevel = (levelConfig.targetLevel || 1) + 2;

    // Try center first, then spiral outward to find unblocked tile
    let x = room.x + Math.floor(room.width / 2);
    let z = room.z + Math.floor(room.depth / 2);
    if (blocked && blocked.has(`${x},${z}`)) {
      const margin = 2;
      for (let attempt = 0; attempt < 15; attempt++) {
        x = room.x + margin + Math.floor(rng() * Math.max(1, room.width - margin * 2));
        z = room.z + margin + Math.floor(rng() * Math.max(1, room.depth - margin * 2));
        if (!blocked.has(`${x},${z}`)) break;
      }
    }
    if (blocked) blocked.add(`${x},${z}`);

    // LPC preset boss — bosses can have unique skin (null = use room skin)
    if (isLPCPreset(characterName)) {
      const seed = SeededRandom.hashString(`${room.id}_boss`);
      const resolved = resolvePreset(characterName, seed, roomSkin, roomHair);
      // Boss archetype: use preset's archetype (arch-mage uses 'mage', not generic 'boss')
      const bossArchetype = resolved.archetype === 'boss' ? 'boss' : resolved.archetype;
      const npcStats = CombatStats.buildNPCStats(bossLevel, bossArchetype, true);

      const combatConfig = {
        ...resolved.combatConfig,
        ...npcStats,
        level: bossLevel,
        isBoss: true,
        isHostile: true
      };

      spawnPoints.push({
        x, y: 0, z,
        type: 'enemy',
        data: {
          characterName,
          lpcAppearance: resolved.lpcAppearance,
          lpcEquipment: resolved.lpcEquipment,
          displayName: resolved.displayName,
          combatConfig,
          roomId: room.id
        }
      });
      return;
    }

    // Legacy boss path
    const scaled = CombatStats.scaleForLevel({ maxHealth: 150, attackPower: 12, defense: 5 }, bossLevel);

    const combatConfig = {
      ...scaled,
      level: bossLevel,
      aggroRange: 12,
      deaggroRange: 18,
      attackRange: 2.2,
      attackCooldown: 1.5,
      lootTable: 'boss',
      isBoss: true,
      isHostile: true
    };

    spawnPoints.push({
      x, y: 0, z,
      type: 'enemy',
      data: {
        characterName,
        combatConfig,
        roomId: room.id
      }
    });
  }

  /** Friendly NPC dialog pools */
  static FRIENDLY_DIALOGS = {
    guide: [
      { text: "Adventurer! The dungeon grows more dangerous the deeper you go. Find the exit portal once you've cleared the enemies." },
      { text: "Watch out for treasure rooms — they hold valuable loot, but are guarded by elite enemies." },
      { text: "Press E near chests to open them. You might find weapons and armor inside." },
      { text: "The purple portal leads to the next level, but you must complete your objectives first." }
    ],
    healer: [
      { text: "Rest here, weary traveler. The potions in this shrine will restore your health." },
      { text: "I've been tending to the wounded in these depths. Take these supplies, you'll need them." },
      { text: "The boss lurks in the deepest chamber. Make sure you're well-equipped before facing it." }
    ],
    merchant: [
      { text: "I got lost down here weeks ago. At least I have my wares to keep me company." },
      { text: "Be careful in the corridors — enemies patrol the halls between rooms." },
      { text: "They say the ancient ruins hold the greatest treasures... and the deadliest creatures." }
    ]
  };

  /** Friendly NPC character types — mix of legacy and LPC */
  static FRIENDLY_TYPES = ['merchant', 'mage', 'surfer', 'lpc-friendly'];

  /**
   * Add a friendly NPC to a room.
   * @private
   */
  static _addFriendlyNPC(spawnPoints, room, rng, role, blocked) {
    const types = DungeonPopulator.FRIENDLY_TYPES;
    const characterName = types[Math.floor(rng() * types.length)];
    const dialogs = DungeonPopulator.FRIENDLY_DIALOGS[role] || DungeonPopulator.FRIENDLY_DIALOGS.guide;
    const dialog = dialogs[Math.floor(rng() * dialogs.length)];

    // Position inside room, 2-tile margin from walls, avoiding blocked tiles
    const margin = 2;
    let x, z;
    for (let attempt = 0; attempt < 15; attempt++) {
      x = room.x + margin + Math.floor(rng() * Math.max(1, room.width - margin * 2));
      z = room.z + margin + Math.floor(rng() * Math.max(1, room.depth - margin * 2));
      if (!blocked || !blocked.has(`${x},${z}`)) break;
    }
    if (blocked) blocked.add(`${x},${z}`);

    const spawnData = {
      x, y: 0, z,
      type: 'friendly',
      data: {
        characterName: characterName === 'lpc-friendly' ? 'lpc' : characterName,
        role,
        dialog: [dialog]
      }
    };

    // Generate LPC appearance for LPC friendly NPCs (random civilian look)
    if (characterName === 'lpc-friendly') {
      const seed = SeededRandom.hashString(`${room.id}_friendly_${role}`);
      const lpcAppearance = generateRandomAppearance({ seed });
      // Give them casual clothing (tunic, no weapons)
      spawnData.data.lpcAppearance = lpcAppearance;
      spawnData.data.lpcEquipment = { body: 'tunic' };
      spawnData.data.displayName = role.charAt(0).toUpperCase() + role.slice(1);
    }

    spawnPoints.push(spawnData);
  }

  /**
   * Generate objectives based on room types present.
   * @private
   */
  static _generateObjectives(level) {
    const objectives = [];

    const hasBoss = level.rooms.some(r => r.type === ROOM_TYPE.BOSS);
    const hasTreasure = level.rooms.some(r => r.type === ROOM_TYPE.TREASURE);

    if (hasBoss) {
      objectives.push({ type: 'kill_boss', label: 'Defeat the Boss', complete: false });
    } else {
      objectives.push({ type: 'kill_all', label: 'Clear all enemies', complete: false });
    }

    if (hasTreasure) {
      objectives.push({ type: 'find_item', label: 'Loot the treasure', itemId: 'dungeon-key', complete: false });
    }

    objectives.push({ type: 'reach_exit', label: 'Reach the exit', complete: false });

    return objectives;
  }
}
